import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { defineMessages, FormattedMessage, injectIntl, intlShape } from 'react-intl';

const messages = defineMessages({
    libraryManagerTitle: {
        id: 'gui.libraryManager.title',
        defaultMessage: 'Library Manager',
        description: 'Title for the library manager tab'
    },
    libraryManagerDesc: {
        id: 'gui.libraryManager.description',
        defaultMessage: 'Search and install official Arduino libraries.',
        description: 'Description for the library manager tab'
    },
    searchPlaceholder: {
        id: 'gui.libraryManager.searchPlaceholder',
        defaultMessage: 'Search Arduino Libraries (e.g. Servo)',
        description: 'Placeholder for library search input'
    },
    searchBtn: {
        id: 'gui.libraryManager.search',
        defaultMessage: 'Search',
        description: 'Search button text'
    },
    searching: {
        id: 'gui.libraryManager.searching',
        defaultMessage: 'Searching...',
        description: 'Text when search is in progress'
    },
    advancedOptionsShow: {
        id: 'gui.libraryManager.advancedOptionsShow',
        defaultMessage: '▶ Advanced Options (Custom Install Path)',
        description: 'Text to show advanced options'
    },
    advancedOptionsHide: {
        id: 'gui.libraryManager.advancedOptionsHide',
        defaultMessage: '▼ Hide Advanced Options',
        description: 'Text to hide advanced options'
    },
    recommended: {
        id: 'gui.libraryManager.recommended',
        defaultMessage: 'Recommended',
        description: 'Recommended tab title'
    },
    installed: {
        id: 'gui.libraryManager.installed',
        defaultMessage: 'Installed ({count})',
        description: 'Installed tab title'
    },
    searchResults: {
        id: 'gui.libraryManager.searchResults',
        defaultMessage: 'Search Results {count}',
        description: 'Search results tab title'
    },
    refresh: {
        id: 'gui.libraryManager.refresh',
        defaultMessage: '↻ Refresh',
        description: 'Refresh button text'
    },
    refreshing: {
        id: 'gui.libraryManager.refreshing',
        defaultMessage: '↻ Refreshing...',
        description: 'Text when refreshing'
    }
});

import styles from './libraries-boards-tab.css';

const RECOMMENDED_LIBRARIES = [
    "Adafruit BusIO", "Adafruit PWM Servo Driver Library", "Adafruit Unified Sensor", 
    "ArduinoJson", "DHT sensor library", "hd44780", "Keypad", "LoRa", "MD_MAX72XX", 
    "NewPing", "PubSubClient", "Servo", "Stepper", "U8g2", "LiquidCrystal"
];

class LibrariesBoardsTab extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            searchQuery: '',
            installPath: '',
            showAdvanced: false,
            libraries: [],
            installedLibraries: new Set(),
            installedLibrariesData: [],
            loading: false,
            refreshing: false,
            fetchError: false,
            installingId: null,
            notificationMessage: null,
            notificationType: 'info',
            activeTab: 'recommended',
            expandedDescriptions: new Set()
        };
        this.handleSearch = this.handleSearch.bind(this);
        this.handleInstall = this.handleInstall.bind(this);
        this.handleUninstall = this.handleUninstall.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handlePathChange = this.handlePathChange.bind(this);
        this.toggleAdvanced = this.toggleAdvanced.bind(this);
        this.fetchInstalledLibraries = this.fetchInstalledLibraries.bind(this);
        this.handleRecommendedClick = this.handleRecommendedClick.bind(this);
        this.renderLibraryCard = this.renderLibraryCard.bind(this);
        this.renderSkeletonCard = this.renderSkeletonCard.bind(this);
        this.switchTab = this.switchTab.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handlePathBlur = this.handlePathBlur.bind(this);
        this.handlePickDirectory = this.handlePickDirectory.bind(this);
        this.showNotification = this.showNotification.bind(this);
        this.toggleDescription = this.toggleDescription.bind(this);
    }

    toggleDescription(libraryName) {
        this.setState(prevState => {
            const newExpanded = new Set(prevState.expandedDescriptions);
            if (newExpanded.has(libraryName)) {
                newExpanded.delete(libraryName);
            } else {
                newExpanded.add(libraryName);
            }
            return { expandedDescriptions: newExpanded };
        });
    }

    showNotification(message, type = 'info', autoClear = true) {
        this.setState({ notificationMessage: message, notificationType: type });
        if (this.notificationTimeout) {
            clearTimeout(this.notificationTimeout);
        }
        if (autoClear) {
            this.notificationTimeout = setTimeout(() => {
                this.setState({ notificationMessage: null });
            }, 4000);
        }
    }

    switchTab (tabName) {
        this.setState({activeTab: tabName});
    }

    handleRefresh () {
        this.fetchInstalledLibraries();
        if (this.state.activeTab === 'searched' && this.state.searchQuery) {
            this.handleSearch({ preventDefault: () => {} });
        }
    }

    handleRecommendedClick (libraryName) {
        this.setState({searchQuery: libraryName}, () => {
            this.handleSearch({ preventDefault: () => {} });
        });
    }

    handlePathBlur () {
        this.fetchInstalledLibraries();
        if (this.state.activeTab === 'searched' && this.state.searchQuery) {
            this.handleSearch({ preventDefault: () => {} });
        }
    }

    async handlePickDirectory () {
        try {
            const response = await fetch('http://localhost:8605/api/libraries/pick-directory');
            const json = await response.json();
            if (json.success && json.path) {
                this.setState({installPath: json.path}, () => {
                    this.handlePathBlur();
                });
            }
        } catch (e) {
            console.error('Failed to pick directory:', e);
            if (e instanceof TypeError || (e.message && e.message.includes('Failed to fetch'))) {
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification('Failed to open directory picker', 'error');
            }
        }
    }

    toggleAdvanced () {
        this.setState(prevState => ({showAdvanced: !prevState.showAdvanced}));
    }

    componentDidMount () {
        this.fetchInstalledLibraries();
    }

    async fetchInstalledLibraries () {
        this.setState({refreshing: true, fetchError: false});
        try {
            const url = `http://localhost:8605/api/libraries/list${this.state.installPath ? `?installPath=${encodeURIComponent(this.state.installPath)}` : ''}`;
            const response = await fetch(url);
            const json = await response.json();
            if (json.success && json.data) {
                const installedSet = new Set();
                const libs = Array.isArray(json.data) ? json.data : (json.data.installed_libraries || []);
                const normalizedLibs = [];
                libs.forEach(item => {
                    const lib = item.library || item;
                    installedSet.add(lib.name);
                    normalizedLibs.push({
                        name: lib.name,
                        latest: {
                            author: lib.author,
                            website: lib.website,
                            sentence: lib.sentence,
                            paragraph: lib.paragraph,
                            version: lib.version
                        }
                    });
                });
                
                this.setState({
                    installedLibraries: installedSet,
                    installedLibrariesData: normalizedLibs
                });
            }
        } catch (error) {
            console.error('Failed to fetch installed libraries:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.setState({fetchError: true});
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            }
        }
        this.setState({refreshing: false});
    }

    handleInputChange (e) {
        this.setState({searchQuery: e.target.value});
    }

    handlePathChange (e) {
        this.setState({installPath: e.target.value});
    }

    async handleSearch (e) {
        e.preventDefault();
        this.setState({loading: true, libraries: [], activeTab: 'searched', fetchError: false});
        try {
            const url = `http://localhost:8605/api/libraries/search?q=${encodeURIComponent(this.state.searchQuery)}${this.state.installPath ? `&installPath=${encodeURIComponent(this.state.installPath)}` : ''}`;
            const response = await fetch(url);
            const json = await response.json();
            if (json.success && json.data && json.data.libraries) {
                this.setState({libraries: json.data.libraries});
            } else {
                this.showNotification('No libraries found.', 'error');
            }
        } catch (error) {
            console.error('Search error:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.setState({fetchError: true});
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification('Failed to connect to local Library Manager server.', 'error');
            }
        }
        this.setState({loading: false});
    }

    async handleInstall (libraryName) {
        this.setState({installingId: libraryName});
        this.showNotification(`Installing ${libraryName}...`, 'info', false);
        try {
            const response = await fetch('http://localhost:8605/api/libraries/install', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    libraryName,
                    installPath: this.state.installPath
                })
            });
            const json = await response.json();
            if (json.success) {
                this.showNotification(`Successfully installed ${libraryName}!`, 'success');
                this.fetchInstalledLibraries(); // Refresh installed list
            } else {
                this.showNotification(`Failed to install ${libraryName}.`, 'error');
            }
        } catch (error) {
            console.error('Install error:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification(`Error installing ${libraryName}.`, 'error');
            }
        }
        this.setState({installingId: null});
    }

    async handleUninstall (libraryName) {
        this.setState({installingId: libraryName});
        this.showNotification(`Uninstalling ${libraryName}...`, 'info', false);
        try {
            const response = await fetch('http://localhost:8605/api/libraries/uninstall', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    libraryName,
                    installPath: this.state.installPath
                })
            });
            const json = await response.json();
            if (json.success) {
                this.showNotification(`Successfully uninstalled ${libraryName}!`, 'success');
                this.fetchInstalledLibraries(); // Refresh installed list
            } else {
                this.showNotification(`Failed to uninstall ${libraryName}.`, 'error');
            }
        } catch (error) {
            console.error('Uninstall error:', error);
            if (error instanceof TypeError || (error.message && error.message.includes('Failed to fetch'))) {
                this.showNotification('Failed to fetch. Make sure agent is running in tray.', 'error');
            } else {
                this.showNotification(`Error uninstalling ${libraryName}.`, 'error');
            }
        }
        this.setState({installingId: null});
    }

    render () {
        return (
            <div className={styles.librariesBoardsContainer}>
                <div className={styles.arduinoManagerSection}>
                    <div className={styles.managerHeader}>
                        <div className={styles.headerTop}>
                            <div className={styles.titleSection}>
                                <h2>{this.props.intl.formatMessage(messages.libraryManagerTitle)}</h2>
                                <p>{this.props.intl.formatMessage(messages.libraryManagerDesc)}</p>
                            </div>
                            
                            <form
                                className={styles.searchForm}
                                onSubmit={this.handleSearch}
                            >
                                <div className={styles.searchRow}>
                                    <input
                                        className={styles.searchInput}
                                        placeholder={this.props.intl.formatMessage(messages.searchPlaceholder)}
                                        type="text"
                                        value={this.state.searchQuery}
                                        onChange={this.handleInputChange}
                                    />
                                    <button
                                        className={styles.searchButton}
                                        type="submit"
                                        disabled={this.state.loading}
                                    >
                                        {this.state.loading ? this.props.intl.formatMessage(messages.searching) : this.props.intl.formatMessage(messages.searchBtn)}
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className={styles.advancedToggleRow}>
                            <span 
                                className={styles.advancedToggle}
                                onClick={this.toggleAdvanced}
                            >
                                {this.state.showAdvanced ? this.props.intl.formatMessage(messages.advancedOptionsHide) : this.props.intl.formatMessage(messages.advancedOptionsShow)}
                            </span>
                            {this.state.showAdvanced && (
                                <div className={styles.advancedOptions}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            className={styles.searchInput}
                                            style={{ flex: 1 }}
                                            placeholder="Optional: Custom Install Path (e.g. C:\Users\name\Documents\Arduino)"
                                            type="text"
                                            value={this.state.installPath}
                                            onChange={this.handlePathChange}
                                            onBlur={this.handlePathBlur}
                                        />
                                        <button 
                                            className={styles.browseButton}
                                            onClick={this.handlePickDirectory}
                                            type="button"
                                        >
                                            Browse...
                                        </button>
                                    </div>
                                    <div className={styles.helpText}>Leave blank to use default Arduino directory. Click outside to apply.</div>
                                </div>
                            )}
                        </div>
                        <div className={styles.tabSwitcher}>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'recommended' })}
                                onClick={() => this.switchTab('recommended')}
                            >
                                {this.props.intl.formatMessage(messages.recommended)}
                            </button>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'installed' })}
                                onClick={() => this.switchTab('installed')}
                            >
                                {this.props.intl.formatMessage(messages.installed, { count: this.state.installedLibrariesData.length })}
                            </button>
                            <button 
                                className={classNames(styles.switcherTab, { [styles.active]: this.state.activeTab === 'searched' })}
                                onClick={() => this.switchTab('searched')}
                            >
                                {this.props.intl.formatMessage(messages.searchResults, { count: this.state.libraries.length > 0 ? `(${this.state.libraries.length})` : '' })}
                            </button>
                            <button
                                className={styles.refreshButton}
                                onClick={this.handleRefresh}
                                disabled={this.state.refreshing || this.state.loading}
                                title={this.props.intl.formatMessage(messages.refresh)}
                            >
                                {this.state.refreshing || this.state.loading ? this.props.intl.formatMessage(messages.refreshing) : this.props.intl.formatMessage(messages.refresh)}
                            </button>
                        </div>
                    </div>

                    <div className={styles.resultsScrollArea}>
                        {this.state.notificationMessage && (
                            <div className={classNames(styles.toastNotification, styles[this.state.notificationType])}>
                                {this.state.notificationMessage}
                            </div>
                        )}

                        <div className={styles.tabContentContainer}>
                            {this.state.activeTab === 'recommended' && (
                                <div className={styles.recommendedSection}>
                                    <h3 className={styles.recommendedTitle}>Recommended Libraries</h3>
                                    <div className={styles.recommendedTags}>
                                        {RECOMMENDED_LIBRARIES.map((lib, idx) => (
                                            <button 
                                                key={idx}
                                                className={styles.recommendedTag}
                                                onClick={() => this.handleRecommendedClick(lib)}
                                            >
                                                {lib}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {this.state.activeTab === 'installed' && (
                                <div className={styles.installedSection}>
                                    <div className={styles.resultsContainer}>
                                        {this.state.installedLibrariesData.length === 0 && !this.state.loading && (
                                            <div className={styles.emptyState}>
                                                {this.state.fetchError ? 'Failed to fetch. Make sure agent is running in tray.' : 'No libraries installed.'}
                                            </div>
                                        )}
                                        {this.state.installedLibrariesData.map(this.renderLibraryCard)}
                                    </div>
                                </div>
                            )}
                            
                            {this.state.activeTab === 'searched' && (
                                <div className={styles.searchedSection}>
                                    <div className={styles.resultsContainer}>
                                        {this.state.loading ? (
                                            Array(6).fill(0).map((_, i) => this.renderSkeletonCard(i))
                                        ) : (
                                            <>
                                                {this.state.libraries.length === 0 && !this.state.notificationMessage && (
                                                    <div className={styles.emptyState}>
                                                        {this.state.fetchError ? 'Failed to fetch. Make sure agent is running in tray.' : 'No search results.'}
                                                    </div>
                                                )}
                                                {this.state.libraries.map(this.renderLibraryCard)}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderSkeletonCard(idx) {
        return (
            <div className={classNames(styles.libraryCard, styles.skeletonCard)} key={`skeleton-${idx}`}>
                <div className={styles.libraryInfo}>
                    <div className={styles.libraryHeader}>
                        <div className={styles.skeletonTitle}></div>
                    </div>
                    <div className={styles.skeletonDesc}></div>
                    <div className={styles.skeletonDesc} style={{width: '60%'}}></div>
                    <div className={styles.skeletonMetaRow}></div>
                </div>
                <div className={styles.libraryActions}>
                    <div className={styles.skeletonVersion}></div>
                    <div className={styles.skeletonButton}></div>
                </div>
            </div>
        );
    }

    renderLibraryCard(lib, idx) {
        const isInstalled = this.state.installedLibraries.has(lib.name);
        const latest = lib.latest || {};
        const isExpanded = this.state.expandedDescriptions.has(lib.name);
        // Simple heuristic: if sentence is long or there is an extra paragraph, it needs expand
        const needsExpand = (latest.sentence && latest.sentence.length > 120) || (latest.paragraph && latest.paragraph !== latest.sentence);

        return (
            <div
                className={styles.libraryCard}
                key={idx}
            >
                <div className={styles.libraryInfo}>
                    <div className={styles.libraryHeader}>
                        <h3 className={styles.libraryTitle}>
                            {lib.name}
                            {isInstalled && (
                                <span className={styles.installedBadge}>
                                    ✓ Installed
                                </span>
                            )}
                        </h3>
                    </div>
                    
                    <div className={styles.libraryMeta}>
                        {latest.author && <span>{latest.author.replace(/<.*?>/g, '').trim()}</span>}
                        {latest.author && latest.website && <span className={styles.metaDot}>•</span>}
                        {latest.website && <a href={latest.website} target="_blank" rel="noopener noreferrer">Website ↗</a>}
                    </div>
                    
                    <div className={styles.libraryDesc}>
                        <strong 
                            className={classNames(styles.libraryDescText, {
                                [styles.expanded]: isExpanded
                            })}
                            dangerouslySetInnerHTML={{ __html: latest.sentence }} 
                        />
                        {needsExpand && (
                            <div 
                                className={styles.readMoreBtn}
                                onClick={() => this.toggleDescription(lib.name)}
                            >
                                {isExpanded ? 'Show less' : 'Read more'}
                            </div>
                        )}
                        {isExpanded && latest.paragraph && latest.paragraph !== latest.sentence && (
                            <div 
                                className={styles.libraryParagraph}
                                dangerouslySetInnerHTML={{ __html: latest.paragraph }}
                            />
                        )}
                    </div>
                </div>
                
                <div className={styles.libraryActions}>
                    {latest.version && <span className={styles.libraryVersion}>v{latest.version}</span>}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {isInstalled && (
                            <button
                                className={styles.uninstallButton}
                                disabled={this.state.installingId === lib.name}
                                onClick={() => this.handleUninstall(lib.name)}
                            >
                                {this.state.installingId === lib.name ? '...' : 'Uninstall'}
                            </button>
                        )}
                        <button
                            className={classNames(styles.installButton, {
                                [styles.isUpdate]: isInstalled
                            })}
                            disabled={this.state.installingId === lib.name}
                            onClick={() => this.handleInstall(lib.name)}
                        >
                            {this.state.installingId === lib.name ? 'Installing...' : (isInstalled ? 'Update / Reinstall' : 'Install')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

LibrariesBoardsTab.propTypes = {
    intl: intlShape.isRequired
};

export default injectIntl(LibrariesBoardsTab);
